import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../utils'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftAddon?: ReactNode
  rightAddon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({
  label,
  error,
  hint,
  leftAddon,
  rightAddon,
  className,
  id,
  ...props
}, ref) {
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-slate-700">
          {label}
          {props.required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        {leftAddon && (
          <div className="pointer-events-none absolute left-3 text-fg-subtle">{leftAddon}</div>
        )}
        <input
          {...props}
          ref={ref}
          id={inputId}
          className={cn(
            'h-9 w-full rounded-lg border border-line bg-surface-raised px-3 text-sm text-fg',
            'placeholder:text-fg-muted',
            'focus:border-accent-600 focus:outline-none focus:ring-2 focus:ring-accent-600/20',
            'disabled:cursor-not-allowed disabled:bg-surface disabled:text-fg-subtle',
            'transition-colors',
            error && 'border-red-400 focus:border-red-500 focus:ring-red-500/20',
            leftAddon && 'pl-9',
            rightAddon && 'pr-9',
            className,
          )}
        />
        {rightAddon && (
          <div className="pointer-events-none absolute right-3 text-fg-subtle">{rightAddon}</div>
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {hint && !error && <p className="text-xs text-fg-muted">{hint}</p>}
    </div>
  )
})

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({
  label, error, hint, className, id, ...props
}, ref) {
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-slate-700">
          {label}
          {props.required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <textarea
        {...props}
        ref={ref}
        id={inputId}
        className={cn(
          'w-full rounded-lg border border-line bg-surface-raised px-3 py-2 text-sm text-fg',
          'placeholder:text-fg-muted',
          'focus:border-accent-600 focus:outline-none focus:ring-2 focus:ring-accent-600/20',
          'disabled:cursor-not-allowed disabled:bg-surface disabled:text-fg-subtle',
          'resize-none transition-colors',
          error && 'border-red-400 focus:border-red-500 focus:ring-red-500/20',
          className,
        )}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      {hint && !error && <p className="text-xs text-fg-muted">{hint}</p>}
    </div>
  )
})
